import { Component, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription, timer } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { NotificationService } from 'core/notification/notification.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { SelectedAccountManagerService } from 'core/selected-account-manager/selected-account-manager.service';
import { UtilsService } from 'core/utils/utils.service';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { DashboardService } from 'src/app/modules/crm/dashboard/services/dashboard.service';
import { OutlookSyncStatus } from 'src/app/modules/shared/entities/outlook-sync-status.enum';
import { TouchpointsService } from './services/touchpoints.service';
import { OutlookSyncResponse } from './entities/outlook-sync-response';
import { EditTouchpointComponent } from './edit-touchpoint/edit-touchpoint.component';
import { TouchpointDetailComponent } from './touchpoint-detail/touchpoint-detail.component';
import {
  ACCOUNT_DASHBOARD_SECTIONS,
  CALENDAR_VIEWS,
  CalendarDateRange,
  CalendarEvent,
  CalendarView,
  CalendarEventFilterOption,
  TimeRangeSelectedEvent,
  Touchpoint,
  TOUCHPOINT_DETAIL_ACTIONS,
  TOUCHPOINT_NAVIGATION_PARAMS,
  TouchpointDetailResult,
  TouchpointListPayload,
  TOUCHPOINTS_TABS,
  TouchpointsTab,
} from './entities/touchpoints-entity';

interface TouchpointEditorPrefill {
  prefillStartDate?: string;
  prefillStartTime?: string;
  prefillEndTime?: string;
}

interface CalendarNavigationEvent {
  selectedDate?: Date;
  direction?: 'previous' | 'next';
  view?: string;
}

@Component({
  selector: 'app-touchpoints',
  templateUrl: './touchpoints.component.html',
  styleUrls: ['./touchpoints.component.scss'],
  standalone: false,
  providers: [DatePipe]
})
export class TouchpointsComponent implements OnInit, AfterViewInit, OnDestroy {
  canEdit = true;
  appStrings = this.appInfoService.appStrings;
  isAdmin = this.appInfoService.isAdminTypeUser();
  isAccountManager = this.appInfoService.isAccountManager();
  isOnline = this.appStateService.state.isOnline;
  isMobile = false;
  readonly tabs = TOUCHPOINTS_TABS;
  activeTab: TouchpointsTab = TOUCHPOINTS_TABS.calendar;
  selectedAccountManagerId: string = null;

  // Account Manager autocomplete
  accountManagerCtrl = new UntypedFormControl('');
  accountManagersList: AccountManager[] = [];
  accountManagerOptions: { value: string, label: string }[] = [];
  accountManagersLoading = false;
  calendarLoading = false;

  /** Record id of the page-selected account manager (the selector holds employeeId; the dialogs expect id). */
  private get selectedAccountManagerRecordId(): string | null {
    return this.accountManagersList.find(am => am.employeeId === this.selectedAccountManagerId)?.id ?? null;
  }

  private get adminSelectedEmployeeId(): string | undefined {
    return this.isAdmin ? (this.selectedAccountManagerId ?? undefined) : undefined;
  }

  // Calendar data
  calendarEvents: CalendarEvent[] = [];
  calendarSelectedDate = new Date();
  calendarView: CalendarView = CALENDAR_VIEWS.week;
  private touchpoints: Touchpoint[] = [];
  private loadedRange: CalendarDateRange | null = null;
  calendarFilterOptions: CalendarEventFilterOption[] = [
    { label: 'Meetings', type: 'meeting', checked: true, color: '#1565C0', backgroundColor: '#E3F2FD' },
    { label: 'Calls', type: 'call', checked: true, color: '#1B5E20', backgroundColor: '#E8F5E9' },
    { label: 'Appointments', type: 'appointment', checked: true, color: '#880E4F', backgroundColor: '#FCE4EC' },
    { label: 'Tasks', type: 'task', checked: true, color: '#E65100', backgroundColor: '#FFF3E0' },
  ];

  // Retry sync
  retryingSync = false;
  syncStatusSubscription: Subscription;
  private pendingOpenTouchpointId: string | null = null;
  private isOpeningRequestedTouchpoint = false;
  private initialTouchpointsLoadCompleted = false;
  private readonly requestedEmployeeId = this.route.snapshot.queryParamMap.get(TOUCHPOINT_NAVIGATION_PARAMS.employeeId);

  toggleValues: { value: string, index: number }[] = [
    { value: this.appInfoService.appStrings?.all_touchpoints ?? '', index: 0 }
  ];

  constructor(
    private appInfoService: AppInfoService,
    private appStateService: AppStateService,
    private datePipe: DatePipe,
    private dialog: MatDialog,
    private dashboardService: DashboardService,
    private touchpointsService: TouchpointsService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private selectedAccountManagerService: SelectedAccountManagerService,
    private utilsService: UtilsService,
  ) {
    this.restoreCalendarFromQueryParams();
    if (this.isOnline && (this.isAccountManager || this.isAdmin)) {
      this.loadAccountManagers();
    }
    if (this.isAccountManager && this.isOnline) {
      this.toggleValues.push({ value: this.appInfoService.appStrings?.label_manageContactsTouchpoints ?? '', index: 1 });
    }
    this.accountManagerCtrl.valueChanges.subscribe(value => {
      if (!value) {
        this.selectedAccountManagerService.clear();
      }
      if (!value || !this.accountManagersList.some(am => am.employeeId === value)) {
        this.selectedAccountManagerId = null;
        this.touchpoints = [];
        this.calendarEvents = [];
        this.loadedRange = null;
      }
    });
  }

  ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    this.route.queryParams.subscribe(params => {
      const touchpointId = params?.[TOUCHPOINT_NAVIGATION_PARAMS.openTouchpointId];
      if (!touchpointId) {
        return;
      }

      this.pendingOpenTouchpointId = touchpointId;
      if (this.initialTouchpointsLoadCompleted) {
        this.openRequestedTouchpoint();
      }
    });
  }

  ngAfterViewInit() {
    if (this.isOnline && this.isAccountManager) {
      return;
    }

    if (!this.calendarLoading) {
      this.loadCalendarEvents();
    }
  }

  ngOnDestroy() {
    this.syncStatusSubscription?.unsubscribe();
  }

  onTabClick(event: any) {
    const previousTab = this.activeTab;
    this.activeTab = event?.index ?? TOUCHPOINTS_TABS.calendar;
    if (this.activeTab === TOUCHPOINTS_TABS.calendar && previousTab !== TOUCHPOINTS_TABS.calendar) {
      this.loadCalendarEvents();
    }
  }

  onAccountManagerSelected(event: MatAutocompleteSelectedEvent) {
    this.selectedAccountManagerId = event?.option?.value ?? null;
    this.selectedAccountManagerService.select(
      this.accountManagersList.find(am => am.employeeId === this.selectedAccountManagerId) ?? null
    );
    this.loadCalendarEvents();
  }

  openNewTouchpoint() {
    this.openTouchpointEditor();
  }

  onCalendarEventClick(event: CalendarEvent): void {
    const touchpoint = this.touchpoints.find(tp => tp.id === event.id);
    if (touchpoint) {
      this.openTouchpointDetail(touchpoint);
    }
  }

  onTimeRangeSelected(range: TimeRangeSelectedEvent) {
    this.openTouchpointEditor({
      prefillStartDate: range.date,
      prefillStartTime: range.startTime,
      prefillEndTime: range.endTime
    });
  }

  private openTouchpointEditor(prefill: TouchpointEditorPrefill = {}) {
    this.dialog.open(EditTouchpointComponent, {
      data: {
        ...prefill,
        accountManagerId: this.selectedAccountManagerRecordId,
        employeeId: this.adminSelectedEmployeeId
      },
      panelClass: ['ds', 'fullWidth', this.isMobile ? 'fullScreen' : ''],
    }).afterClosed().subscribe(result => {
      if (result) {
        this.loadCalendarEvents();
      }
    });
  }

  onMonthAddEvent(date: Date) {
    this.openTouchpointEditor({
      prefillStartDate: this.datePipe.transform(date, 'yyyy-MM-dd') ?? undefined
    });
  }

  onCalendarDateSelected(date: Date) {
    this.updateCalendarSelectionAndReload(date);
  }

  onCalendarTodayClicked(date: Date) {
    this.updateCalendarSelectionAndReload(date);
  }

  onCalendarPeriodNavigated(event: CalendarNavigationEvent) {
    this.updateCalendarSelectionAndReload(event?.selectedDate, event?.view);
  }

  onCalendarViewChanged(view: string) {
    this.updateCalendarSelectionAndReload(undefined, view);
  }

  retrySync() {
    this.retryingSync = true;
    this.appInfoService.retrySyncOutlook().subscribe((resp: OutlookSyncResponse) => {
      if (resp.status === OutlookSyncStatus.InProgress) {
        this.notificationService.showNotification(resp.message, NotificationType.Warning, true);
        this.retryingSync = false;
      } else {
        this.checkSyncStatus();
      }
    });
  }

  private checkSyncStatus() {
    this.syncStatusSubscription = timer(0, 5000).pipe(
      switchMap(() => this.touchpointsService.getOutlookSyncStatus())
    ).subscribe((resp: OutlookSyncResponse) => {
      if (resp.status === OutlookSyncStatus.Completed || resp.status === OutlookSyncStatus.Failed) {
        this.syncStatusSubscription.unsubscribe();
        const type = resp.status === OutlookSyncStatus.Failed ? NotificationType.Error : NotificationType.Success;
        this.notificationService.showNotification(resp.message, type, true);
        this.retryingSync = false;
        if (resp.status === OutlookSyncStatus.Completed) {
          this.loadCalendarEvents();
        }
      }
    });
  }

  private loadCalendarEvents(date: Date = this.calendarSelectedDate ?? new Date()) {
    const activeDate = date ?? this.calendarSelectedDate ?? new Date();
    if (!this.selectedAccountManagerId && !this.isAccountManager) {
      this.touchpoints = [];
      this.calendarEvents = [];
      this.loadedRange = null;
      this.initialTouchpointsLoadCompleted = true;
      this.openRequestedTouchpoint();
      return;
    }

    this.calendarLoading = true;
    this.initialTouchpointsLoadCompleted = false;
    this.calendarSelectedDate = activeDate;
    const range = this.getCalendarDateRange(activeDate);
    const payload: TouchpointListPayload = {
      touchpointTypes: [1, 2, 3, 4],
      date: {
        startDate: range.StartDate.toISOString(),
        endDate: range.EndDate.toISOString()
      }
    };
    if (this.selectedAccountManagerId) {
      payload.employeeId = this.selectedAccountManagerId;
    }
    this.touchpointsService.fetchTouchpoints(payload).pipe(
      finalize(() => {
        this.calendarLoading = false;
        this.initialTouchpointsLoadCompleted = true;
        this.openRequestedTouchpoint();
      })
    ).subscribe({
      next: touchpoints => {
        this.loadedRange = range;
        this.touchpoints = touchpoints;
        this.calendarEvents = touchpoints.map(tp => this.mapTouchpointToCalendarEvent(tp));
      },
      error: () => {
        this.loadedRange = null;
        this.touchpoints = [];
        this.calendarEvents = [];
      }
    });
  }

  private updateCalendarSelectionAndReload(date?: Date | null, view?: string): void {
    this.calendarSelectedDate = date ?? this.calendarSelectedDate ?? new Date();
    if (view !== undefined) {
      this.calendarView = this.normalizeCalendarView(view);
    }

    if (this.isRangeLoaded(this.getCalendarDateRange(this.calendarSelectedDate))) {
      return;
    }

    this.loadCalendarEvents(this.calendarSelectedDate);
  }

  private isRangeLoaded(range: CalendarDateRange): boolean {
    return !!this.loadedRange
      && range.StartDate.getTime() >= this.loadedRange.StartDate.getTime()
      && range.EndDate.getTime() <= this.loadedRange.EndDate.getTime();
  }

  private restoreCalendarFromQueryParams(): void {
    const params = this.route.snapshot.queryParamMap;
    const date = this.utilsService.parseLocalDate(params.get(TOUCHPOINT_NAVIGATION_PARAMS.calendarDate) ?? undefined);
    if (date) {
      this.calendarSelectedDate = date;
    }
  }

  private normalizeCalendarView(view: string): CalendarView {
    return Object.values(CALENDAR_VIEWS).find(known => known === view) ?? CALENDAR_VIEWS.week;
  }

  private getCalendarDateRange(date: Date): CalendarDateRange {
    if (this.calendarView === CALENDAR_VIEWS.day) {
      const start = new Date(date);
      const end = new Date(date);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return { StartDate: start, EndDate: end };
    }

    if (this.calendarView === CALENDAR_VIEWS.month) {
      return this.touchpointsService.getMonthGridForDay(date);
    }

    return this.touchpointsService.getWeekForDay(date);
  }

  private mapTouchpointToCalendarEvent(tp: Touchpoint): CalendarEvent {
    const categoryMap: Record<number, string> = { 1: 'meeting', 2: 'call', 3: 'appointment', 4: 'task' };
    const displayMap: Record<number, string> = { 1: 'Meeting', 2: 'Call', 3: 'Appointment', 4: 'Task' };
    return {
      id: tp.id,
      date: this.datePipe.transform(tp.startDate, 'yyyy-MM-dd') ?? '',
      startTime: this.datePipe.transform(tp.startDate, 'HH:mm') ?? '00:00',
      endTime: this.datePipe.transform(tp.endDate, 'HH:mm') ?? '00:00',
      title: `${tp.subject} (${displayMap[tp.touchpointType] ?? ''})`,
      category: categoryMap[tp.touchpointType] ?? '',
      hasAlert: tp.alarmType !== null && tp.alarmType !== 0,
      hasRecurrence: tp.isRecurring ?? false,
      hasAttachment: tp.hasAttachments ?? false
    };
  }

  private openRequestedTouchpoint(): void {
    if (!this.pendingOpenTouchpointId || this.isOpeningRequestedTouchpoint || this.accountManagersLoading) {
      return;
    }

    if (!this.initialTouchpointsLoadCompleted && this.calendarLoading) {
      return;
    }

    const requestedTouchpoint = this.touchpoints.find(tp => tp.id === this.pendingOpenTouchpointId);
    if (requestedTouchpoint) {
      this.openTouchpointDetail(requestedTouchpoint);
      return;
    }

    this.isOpeningRequestedTouchpoint = true;
    this.touchpointsService.getTouchpoint(this.pendingOpenTouchpointId, this.adminSelectedEmployeeId).subscribe({
      next: touchpoint => {
        this.isOpeningRequestedTouchpoint = false;
        if (!touchpoint) {
          this.pendingOpenTouchpointId = null;
          return;
        }

        this.openTouchpointDetail(touchpoint);
      },
      error: () => {
        this.isOpeningRequestedTouchpoint = false;
      }
    });
  }

  private openTouchpointDetail(touchpoint: Touchpoint): void {
    this.dialog.open(TouchpointDetailComponent, {
      data: {
        ...touchpoint,
        accountManagerId: this.selectedAccountManagerRecordId,
        employeeId: this.adminSelectedEmployeeId
      },
      ...(this.isMobile ? {} : { width: '640px' }),
      disableClose: false,
      panelClass: ['ds', 'fullWidth', this.isMobile ? 'fullScreen' : ''],
      autoFocus: true,
      restoreFocus: true,
    }).afterClosed().subscribe((result?: TouchpointDetailResult) => this.onTouchpointDetailClosed(touchpoint, result));
  }

  private onTouchpointDetailClosed(touchpoint: Touchpoint, result?: TouchpointDetailResult): void {
    this.pendingOpenTouchpointId = null;
    if (result?.action) {
      this.navigateToAccountDashboard(touchpoint, result.action === TOUCHPOINT_DETAIL_ACTIONS.lastVisited);
      return;
    }

    this.clearNavigationQueryParams();
    if (result?.deleted || result?.edited) {
      this.loadCalendarEvents();
    }
  }

  private navigateToAccountDashboard(touchpoint: Touchpoint, scrollToNotes: boolean): void {
    const accountDashboardUrl = this.touchpointsService.getAccountDashboardUrl(touchpoint);
    if (!accountDashboardUrl) {
      return;
    }
    this.router.navigate([accountDashboardUrl], {
      queryParams: scrollToNotes ? { [TOUCHPOINT_NAVIGATION_PARAMS.section]: ACCOUNT_DASHBOARD_SECTIONS.notes } : {}
    });
  }

  private clearNavigationQueryParams(): void {
    const { openTouchpointId, calendarDate, employeeId } = TOUCHPOINT_NAVIGATION_PARAMS;
    this.router.navigate([], {
      queryParams: { [openTouchpointId]: null, [calendarDate]: null, [employeeId]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private loadAccountManagers() {
    this.accountManagersLoading = true;
    this.dashboardService.getAccountManagers((list: AccountManager[]) => {
      this.accountManagerOptions = list.map(am => ({ value: am.employeeId, label: am.name }));
      this.accountManagersList = list;
      this.accountManagersLoading = false;

      this.preselectAccountManager(list);
      if (this.selectedAccountManagerId || this.isAccountManager) {
        this.loadCalendarEvents();
      } else {
        this.openRequestedTouchpoint();
      }
    }, () => {
      this.accountManagersLoading = false;
      this.openRequestedTouchpoint();
    });
  }

  private preselectAccountManager(list: AccountManager[]): void {
    const requested = this.isAdmin ? list.find(am => am.employeeId === this.requestedEmployeeId) : undefined;
    if (requested) {
      this.selectedAccountManagerService.select(requested);
    }

    const preselectedId = requested?.employeeId ?? this.selectedAccountManagerService.selected?.employeeId;
    if (preselectedId && list.some(am => am.employeeId === preselectedId)) {
      this.selectedAccountManagerId = preselectedId;
      this.accountManagerCtrl.setValue(preselectedId, { emitEvent: false });
    } else if (this.isAccountManager) {
      this.selectedAccountManagerId = this.appInfoService.userData?.UserInfo?.EmployeeId ?? null;
    }
  }
}
